import { useState, useEffect, useMemo, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { QuoteTemplate } from '@/types/quote';
import { Upload, FileText, Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { PDFTemplateDesigner } from './quote/PDFTemplateDesigner';
import { StandardTermsSelector } from './quote/StandardTermsSelector';
import { useTemplateTerms } from '@/hooks/useStandardTerms';
import { getDocument } from 'pdfjs-dist';

interface QuoteTemplateModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  template?: QuoteTemplate | null;
  onSave: (data: Partial<QuoteTemplate>) => Promise<void>;
}

export const QuoteTemplateModal = ({
  open,
  onOpenChange,
  template,
  onSave
}: QuoteTemplateModalProps) => {
  const [formData, setFormData] = useState<Partial<QuoteTemplate>>({
    name: '',
    description: '',
    template_body: '',
    template_terms: '',
    header_logo_url: '',
    background_image_url: '',
    brand_primary: '#014336',
    brand_secondary: '#10B981',
    footer_text: '',
    is_default: false,
    pdf_template_url: null,
    pdf_page_count: null,
    placeholder_positions: null
  });
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('basic');
  const initialDataRef = useRef<string | null>(null);

  // Track unsaved changes
  const hasUnsavedChanges = useMemo(() => {
    if (!open || initialDataRef.current === null) return false;
    return JSON.stringify(formData) !== initialDataRef.current;
  }, [formData, open]);

  useEffect(() => {
    if (open) {
      if (template) {
        setFormData(template);
        initialDataRef.current = JSON.stringify(template);
      } else {
        const defaultData = {
          name: '',
          description: '',
          template_body: '',
          template_terms: '',
          header_logo_url: '',
          background_image_url: '',
          brand_primary: '#014336',
          brand_secondary: '#10B981',
          footer_text: '',
          is_default: false,
          pdf_template_url: null,
          pdf_page_count: null,
          placeholder_positions: null
        };
        setFormData(defaultData);
        initialDataRef.current = JSON.stringify(defaultData);
      }
      setActiveTab('basic');
    } else {
      initialDataRef.current = null;
    }
  }, [template, open]);

  // Handle close with unsaved changes warning
  const handleClose = (openState: boolean) => {
    if (!openState && hasUnsavedChanges) {
      if (!confirm('Je hebt onopgeslagen wijzigingen. Weet je zeker dat je wilt sluiten?')) {
        return;
      }
    }
    onOpenChange(openState);
  };

  const handleUploadImage = async (file: File, type: 'logo' | 'background') => {
    try {
      setUploading(true);
      const fileExt = file.name.split('.').pop();
      const fileName = `${Math.random()}.${fileExt}`;
      const filePath = `quote-templates/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('documents')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('documents')
        .getPublicUrl(filePath);

      setFormData(prev => ({
        ...prev,
        [type === 'logo' ? 'header_logo_url' : 'background_image_url']: publicUrl
      }));

      toast.success('Afbeelding geüpload');
    } catch (error: any) {
      console.error('Error uploading image:', error);
      toast.error('Fout bij uploaden afbeelding', {
        description: error.message
      });
    } finally {
      setUploading(false);
    }
  };

  const handlePDFUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || file.type !== 'application/pdf') {
      toast.error('Upload een geldig PDF bestand');
      return;
    }

    setUploading(true);
    try {
      // 1. Upload naar Supabase Storage
      const fileExt = 'pdf';
      const fileName = `${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;
      const filePath = `templates/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('quote-templates')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) throw uploadError;

      // 2. Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('quote-templates')
        .getPublicUrl(filePath);

      // 3. Get page count using pdfjs-dist
      const arrayBuffer = await file.arrayBuffer();
      const pdf = await getDocument({ data: arrayBuffer }).promise;
      const pageCount = pdf.numPages;

      const updatedData = {
        ...formData,
        pdf_template_url: publicUrl,
        pdf_page_count: pageCount,
        // Behoud bestaande page_count als deze hoger is, anders gebruik PDF page count
        page_count: Math.max(formData.page_count || 1, pageCount),
      };

      setFormData(updatedData);

      // Auto-save to database if editing existing template
      if (template?.id) {
        try {
          setSaving(true);
          await onSave(updatedData);
          initialDataRef.current = JSON.stringify(updatedData);
          toast.success(`PDF geüpload en opgeslagen (${pageCount} pagina's)`);
        } catch (error) {
          toast.error('PDF geüpload maar niet opgeslagen naar database');
        } finally {
          setSaving(false);
        }
      } else {
        toast.success(`PDF geüpload (${pageCount} pagina's)`);
      }

      setActiveTab('designer');
    } catch (error: any) {
      console.error('Upload error:', error);
      toast.error('PDF upload mislukt');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setSaving(true);
      await onSave(formData);
      initialDataRef.current = JSON.stringify(formData);
      onOpenChange(false);
    } catch (error) {
      // Error handled in onSave
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {template ? 'Template Bewerken' : 'Nieuw Template'}
            {hasUnsavedChanges && (
              <Badge variant="outline" className="text-orange-600 border-orange-600">
                Niet opgeslagen
              </Badge>
            )}
            {saving && (
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            )}
          </DialogTitle>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col overflow-hidden">
          <TabsList className="w-full">
            <TabsTrigger value="basic">Basis Info</TabsTrigger>
            <TabsTrigger value="pdf">PDF Template</TabsTrigger>
            {/* Show designer tab when PDF exists OR when page_count > 0 (blank multi-page templates) */}
            {(formData.pdf_template_url || (formData.page_count && formData.page_count > 0)) && (
              <TabsTrigger value="designer">Placeholder Designer</TabsTrigger>
            )}
          </TabsList>

          <div className="flex-1 overflow-y-auto">
            <TabsContent value="basic" className="space-y-4 p-1">
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Naam *</Label>
                  <Input
                    id="name"
                    value={formData.name || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">Beschrijving</Label>
                  <Textarea
                    id="description"
                    value={formData.description || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    rows={2}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="template_body">Template Inhoud</Label>
                  <Textarea
                    id="template_body"
                    value={formData.template_body || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, template_body: e.target.value }))}
                    rows={4}
                    placeholder="Gebruik placeholders zoals {{organization_name}}, {{contact_name}}, etc."
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="template_terms">Template Voorwaarden</Label>
                  <Textarea
                    id="template_terms"
                    value={formData.template_terms || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, template_terms: e.target.value }))}
                    rows={4}
                  />
                </div>

                <div className="border-t pt-4 space-y-4">
                  <h3 className="font-medium">Branding</h3>

                  {/* ... keep existing branding code ... */}
                  <div className="space-y-2">
                    <Label>Header Logo</Label>
                    <div className="flex gap-2">
                      <Input
                        value={formData.header_logo_url || ''}
                        onChange={(e) => setFormData(prev => ({ ...prev, header_logo_url: e.target.value }))}
                        placeholder="URL of upload een afbeelding"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        disabled={uploading}
                        onClick={() => {
                          const input = document.createElement('input');
                          input.type = 'file';
                          input.accept = 'image/*';
                          input.onchange = (e) => {
                            const file = (e.target as HTMLInputElement).files?.[0];
                            if (file) handleUploadImage(file, 'logo');
                          };
                          input.click();
                        }}
                      >
                        <Upload className="h-4 w-4" />
                      </Button>
                    </div>
                    {formData.header_logo_url && (
                      <img src={formData.header_logo_url} alt="Logo preview" className="h-16 object-contain" />
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label>Achtergrond Afbeelding</Label>
                    <div className="flex gap-2">
                      <Input
                        value={formData.background_image_url || ''}
                        onChange={(e) => setFormData(prev => ({ ...prev, background_image_url: e.target.value }))}
                        placeholder="URL of upload een afbeelding"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        disabled={uploading}
                        onClick={() => {
                          const input = document.createElement('input');
                          input.type = 'file';
                          input.accept = 'image/*';
                          input.onchange = (e) => {
                            const file = (e.target as HTMLInputElement).files?.[0];
                            if (file) handleUploadImage(file, 'background');
                          };
                          input.click();
                        }}
                      >
                        <Upload className="h-4 w-4" />
                      </Button>
                    </div>
                    {formData.background_image_url && (
                      <img src={formData.background_image_url} alt="Background preview" className="h-24 object-cover w-full" />
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="brand_primary">Primaire Kleur</Label>
                      <div className="flex gap-2">
                        <Input
                          id="brand_primary"
                          type="color"
                          value={formData.brand_primary || '#014336'}
                          onChange={(e) => setFormData(prev => ({ ...prev, brand_primary: e.target.value }))}
                          className="w-16 h-10 p-1"
                        />
                        <Input
                          value={formData.brand_primary || '#014336'}
                          onChange={(e) => setFormData(prev => ({ ...prev, brand_primary: e.target.value }))}
                          placeholder="#014336"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="brand_secondary">Secundaire Kleur</Label>
                      <div className="flex gap-2">
                        <Input
                          id="brand_secondary"
                          type="color"
                          value={formData.brand_secondary || '#10B981'}
                          onChange={(e) => setFormData(prev => ({ ...prev, brand_secondary: e.target.value }))}
                          className="w-16 h-10 p-1"
                        />
                        <Input
                          value={formData.brand_secondary || '#10B981'}
                          onChange={(e) => setFormData(prev => ({ ...prev, brand_secondary: e.target.value }))}
                          placeholder="#10B981"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="footer_text">Footer Tekst</Label>
                    <Textarea
                      id="footer_text"
                      value={formData.footer_text || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, footer_text: e.target.value }))}
                      rows={2}
                      placeholder="Bijvoorbeeld: Bedrijfsnaam | KvK: 12345678 | BTW: NL123456789B01"
                    />
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  <Switch
                    id="is_default"
                    checked={formData.is_default || false}
                    onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_default: checked }))}
                  />
                  <Label htmlFor="is_default">Standaard template</Label>
                </div>

                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                    Annuleren
                  </Button>
                  <Button type="submit">
                    {template ? 'Bijwerken' : 'Aanmaken'}
                  </Button>
                </DialogFooter>
              </form>
            </TabsContent>

            <TabsContent value="pdf" className="space-y-4 p-1">
              <div className="space-y-6">
                {/* PDF Template Upload */}
                <div>
                  <Label>PDF Template Upload</Label>
                  <p className="text-sm text-muted-foreground mb-2">
                    Upload een PDF die als achtergrond dient voor je offerte
                  </p>
                  
                  <Input
                    type="file"
                    accept="application/pdf"
                    onChange={handlePDFUpload}
                    disabled={uploading}
                  />
                  
                  {formData.pdf_template_url && (
                    <div className="mt-4 p-4 border rounded">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <FileText className="h-5 w-5" />
                          <div>
                            <p className="text-sm font-medium">PDF geüpload</p>
                            <p className="text-xs text-muted-foreground">
                              {formData.pdf_page_count} pagina's
                            </p>
                          </div>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => window.open(formData.pdf_template_url!, '_blank')}
                        >
                          Bekijken
                        </Button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Terms Document Upload */}
                <div className="border-t pt-4">
                  <Label>Algemene Voorwaarden (PDF)</Label>
                  <p className="text-sm text-muted-foreground mb-2">
                    Upload je algemene voorwaarden als PDF - deze worden automatisch als laatste pagina's toegevoegd aan de offerte
                  </p>
                  
                  <Input
                    type="file"
                    accept="application/pdf"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file || file.type !== 'application/pdf') {
                        toast.error('Upload een geldig PDF bestand');
                        return;
                      }

                      setUploading(true);
                      try {
                        const fileName = `${Date.now()}-terms-${Math.random().toString(36).substring(7)}.pdf`;
                        const filePath = `templates/terms/${fileName}`;

                        const { error: uploadError } = await supabase.storage
                          .from('quote-templates')
                          .upload(filePath, file, {
                            cacheControl: '3600',
                            upsert: false
                          });

                        if (uploadError) throw uploadError;

                        const { data: { publicUrl } } = supabase.storage
                          .from('quote-templates')
                          .getPublicUrl(filePath);

                        const updatedData = {
                          ...formData,
                          terms_document_url: publicUrl
                        };

                        setFormData(updatedData);

                        // Auto-save if editing existing template
                        if (template?.id) {
                          try {
                            setSaving(true);
                            await onSave(updatedData);
                            initialDataRef.current = JSON.stringify(updatedData);
                            toast.success('Algemene voorwaarden PDF geüpload en opgeslagen');
                          } catch (error) {
                            toast.error('PDF geüpload maar niet opgeslagen naar database');
                          } finally {
                            setSaving(false);
                          }
                        } else {
                          toast.success('Algemene voorwaarden PDF geüpload');
                        }
                      } catch (error: any) {
                        console.error('Upload error:', error);
                        toast.error('PDF upload mislukt');
                      } finally {
                        setUploading(false);
                      }
                    }}
                    disabled={uploading}
                  />
                  
                  {formData.terms_document_url && (
                    <div className="mt-4 p-4 border rounded bg-muted/50">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <FileText className="h-5 w-5 text-emerald-600" />
                          <div>
                            <p className="text-sm font-medium">Voorwaarden PDF geüpload</p>
                            <p className="text-xs text-muted-foreground">
                              Wordt als laatste pagina's toegevoegd
                            </p>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => window.open(formData.terms_document_url!, '_blank')}
                          >
                            Bekijken
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setFormData(prev => ({ ...prev, terms_document_url: null }))}
                            className="text-destructive hover:text-destructive"
                          >
                            Verwijderen
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {uploading && (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-8 w-8 animate-spin" />
                  </div>
                )}
              </div>
            </TabsContent>

            {/* Show designer when PDF exists OR when we have page_count (blank pages) */}
            {(formData.pdf_template_url || (formData.page_count && formData.page_count > 0)) && (
              <TabsContent value="designer" className="p-0 m-0">
                <PDFTemplateDesigner
                  pdfUrl={formData.pdf_template_url || null}
                  initialPositions={formData.placeholder_positions || []}
                  pageCount={formData.page_count || 1}
                  pageBackgrounds={formData.page_backgrounds || []}
                  onSave={async (positions, newPageCount, newPageBackgrounds) => {
                    const updatedData = {
                      ...formData,
                      placeholder_positions: positions,
                      page_count: newPageCount,
                      page_backgrounds: newPageBackgrounds,
                    };
                    setFormData(updatedData);

                    // Auto-save to database if editing existing template
                    if (template?.id) {
                      try {
                        setSaving(true);
                        await onSave(updatedData);
                        initialDataRef.current = JSON.stringify(updatedData);
                        toast.success('Template opgeslagen naar database');
                      } catch (error) {
                        toast.error('Fout bij opslaan template');
                      } finally {
                        setSaving(false);
                      }
                    } else {
                      toast.success('Template configuratie opgeslagen (sla template op om te bewaren)');
                    }

                    setActiveTab('basic');
                  }}
                  onCancel={() => setActiveTab('basic')}
                />
              </TabsContent>
            )}
          </div>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};
